import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Images } from "../../../../../../../utils/imageConstants";
import { Api } from "../../../../../../../services/interceptor";
import { QUERY_KEY } from "../../../../../../../services/queryEndPoint";
import type {
  HeadToHeadStatsType,
  IBatsmanVsBowlerResponse,
} from "../types/batsmanVsBowler.d";

interface SelectedPlayer {
  id: string | null;
  name: string;
}

interface EachOtherTabProps {
  leftPlayer: SelectedPlayer;
  rightPlayer: SelectedPlayer;
  matchId?: string;
  tournamentId?: string;
}

const FILTER_OPTIONS: Array<{
  value: HeadToHeadStatsType;
  label: string;
  helper: string;
}> = [
  {
    value: "all",
    label: "All Time",
    helper: "Across every recorded match",
  },
  {
    value: "tournament",
    label: "Tournament",
    helper: "Scoped to this tournament when available",
  },
  {
    value: "match",
    label: "This Match",
    helper: "Scoped to the current match when available",
  },
];

const getSafeNumber = (value?: number | null) => Number(value ?? 0);

const formatStrikeRate = (value?: number | null) => getSafeNumber(value).toFixed(2);

const getScopeLabel = (statsType: HeadToHeadStatsType) =>
  FILTER_OPTIONS.find((option) => option.value === statsType)?.label ?? "All Time";

const formatMetricValue = (value: number | string) =>
  typeof value === "number" ? (Number.isInteger(value) ? String(value) : value.toFixed(2)) : value;

const getEconomy = (runs?: number | null, balls?: number | null) => {
  const safeBalls = getSafeNumber(balls);
  if (!safeBalls) return 0;
  return (getSafeNumber(runs) * 6) / safeBalls;
};

const getBattingAverage = (runs?: number | null, outs?: number | null) => {
  const safeOuts = getSafeNumber(outs);
  if (!safeOuts) return "-";
  return Number((getSafeNumber(runs) / safeOuts).toFixed(2));
};

const getBowlingStrikeRate = (balls?: number | null, wickets?: number | null) => {
  const safeWickets = getSafeNumber(wickets);
  if (!safeWickets) return "-";
  return Number((getSafeNumber(balls) / safeWickets).toFixed(2));
};

const buildHeadToHeadParams = (
  batsmanId: string,
  bowlerId: string,
  statsType: HeadToHeadStatsType,
  tournamentId?: string,
  matchId?: string
) => ({
  batsmanId,
  bowlerId,
  statsType,
  ...(statsType === "tournament" && tournamentId ? { tournamentId } : {}),
  ...(statsType === "match" && matchId ? { matchId } : {}),
});

const EachOtherTab = ({
  leftPlayer,
  rightPlayer,
  matchId,
  tournamentId,
}: EachOtherTabProps) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pendingStatsType, setPendingStatsType] = useState<HeadToHeadStatsType>("all");
  const [appliedStatsType, setAppliedStatsType] = useState<HeadToHeadStatsType>("all");
  const panelId = useId();
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const chipText = useMemo(() => getScopeLabel(appliedStatsType), [appliedStatsType]);
  const fallbackScopeMessage = useMemo(() => {
    if (appliedStatsType === "tournament" && !tournamentId) {
      return "Tournament context is unavailable, so the API falls back to all-time stats.";
    }

    if (appliedStatsType === "match" && !matchId) {
      return "Match context is unavailable, so the API falls back to all-time stats.";
    }

    return "";
  }, [appliedStatsType, matchId, tournamentId]);

  const hasSelectedPlayers = !!leftPlayer.id && !!rightPlayer.id;

  const batterVsBowlerQuery = useQuery<IBatsmanVsBowlerResponse>({
    queryKey: [
      QUERY_KEY.batsmanVsBowler,
      leftPlayer.id,
      rightPlayer.id,
      appliedStatsType,
      tournamentId,
      matchId,
      "left-batsman",
    ],
    enabled: hasSelectedPlayers,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.batsmanVsBowler, {
        params: buildHeadToHeadParams(
          leftPlayer.id as string,
          rightPlayer.id as string,
          appliedStatsType,
          tournamentId,
          matchId
        ),
      });

      return response.data as IBatsmanVsBowlerResponse;
    },
  });

  const bowlerVsBatterQuery = useQuery<IBatsmanVsBowlerResponse>({
    queryKey: [
      QUERY_KEY.batsmanVsBowler,
      rightPlayer.id,
      leftPlayer.id,
      appliedStatsType,
      tournamentId,
      matchId,
      "right-batsman",
    ],
    enabled: hasSelectedPlayers,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.batsmanVsBowler, {
        params: buildHeadToHeadParams(
          rightPlayer.id as string,
          leftPlayer.id as string,
          appliedStatsType,
          tournamentId,
          matchId
        ),
      });

      return response.data as IBatsmanVsBowlerResponse;
    },
  });

  const handleReset = () => {
    setPendingStatsType("all");
    setAppliedStatsType("all");
    setIsFilterOpen(false);
  };

  const handleApply = () => {
    setAppliedStatsType(pendingStatsType);
    setIsFilterOpen(false);
  };

  const buildRows = (
    data: IBatsmanVsBowlerResponse | undefined,
    perspective: "left-batting" | "right-batting"
  ) => {
    const runs = getSafeNumber(data?.runsScored);
    const balls = getSafeNumber(data?.ballsPlayed);
    const strikeRate = Number(formatStrikeRate(data?.strikeRate));
    const wickets = getSafeNumber(data?.outs);
    const economy = getEconomy(data?.runsScored, data?.ballsPlayed);
    const battingAverage = getBattingAverage(data?.runsScored, data?.outs);
    const bowlingStrikeRate = getBowlingStrikeRate(data?.ballsPlayed, data?.outs);
    const sharedRows = [
      { title: "CATCHES TAKEN", leftValue: "0", rightValue: "0" },
      { title: "STUMPINGS", leftValue: "0", rightValue: "0" },
      { title: "RUN OUTS", leftValue: "0", rightValue: "0" },
    ];

    if (perspective === "left-batting") {
      return [
        {
          title: "RUNS SCORED/BALLS BOWLED",
          leftValue: formatMetricValue(runs),
          rightValue: formatMetricValue(balls),
        },
        {
          title: "BATTING SR/ECONOMY",
          leftValue: formatMetricValue(strikeRate),
          rightValue: formatMetricValue(economy),
        },
        {
          title: "WICKETS TAKEN",
          leftValue: "-",
          rightValue: formatMetricValue(wickets),
          highlightRight: true,
        },
        {
          title: "BATTING AVE/BOWLING SR",
          leftValue: formatMetricValue(battingAverage),
          rightValue: formatMetricValue(bowlingStrikeRate),
        },
        ...sharedRows,
      ];
    }

    return [
      {
        title: "BALLS BOWLED/RUNS SCORED",
        leftValue: formatMetricValue(balls),
        rightValue: formatMetricValue(runs),
      },
      {
        title: "ECONOMY/BATTING SR",
        leftValue: formatMetricValue(economy),
        rightValue: formatMetricValue(strikeRate),
      },
      {
        title: "WICKETS TAKEN",
        leftValue: formatMetricValue(wickets),
        rightValue: "-",
        highlightLeft: true,
      },
      {
        title: "BOWLING SR/BATTING AVE",
        leftValue: formatMetricValue(bowlingStrikeRate),
        rightValue: formatMetricValue(battingAverage),
      },
      ...sharedRows,
    ];
  };

  const renderSection = ({
    sectionTitle,
    leftLabel,
    rightLabel,
    leftIcon,
    rightIcon,
    data,
    isLoading,
    isError,
    batsmanName,
    bowlerName,
    perspective,
  }: {
    sectionTitle: string;
    leftLabel: string;
    rightLabel: string;
    leftIcon: string;
    rightIcon: string;
    data?: IBatsmanVsBowlerResponse;
    isLoading: boolean;
    isError: boolean;
    batsmanName: string;
    bowlerName: string;
    perspective: "left-batting" | "right-batting";
  }) => (
    <>
      <header className="header-wrap" aria-label={sectionTitle}>
        <div className="nav-text-parent">
          <p className="nav-text">{leftLabel}</p>
          <img className="icon" alt="" aria-hidden="true" src={leftIcon} />
        </div>
        <div className="nav-text-parent">
          <img className="icon" alt="" aria-hidden="true" src={rightIcon} />
          <p className="nav-text">{rightLabel}</p>
        </div>
      </header>

      <section className="score-wrap" aria-label={sectionTitle}>
        <section className="matches-wrapper each-other-matches-wrapper" aria-label={`${batsmanName} against ${bowlerName}`}>
          <div className="each-other-summary">
            <p className="matchup-label">{batsmanName} vs {bowlerName}</p>
            <p className="scope-label">{chipText}</p>
            {fallbackScopeMessage ? <p className="scope-note">{fallbackScopeMessage}</p> : null}
          </div>

          {!hasSelectedPlayers ? (
            <div className="each-other-status">
              <p>Select both players above to view head-to-head stats.</p>
            </div>
          ) : isLoading ? (
            <div className="each-other-status">
              <p>Loading head-to-head stats...</p>
            </div>
          ) : isError ? (
            <div className="each-other-status">
              <p>We couldn't load the head-to-head stats right now.</p>
            </div>
          ) : (
            <div className="match-comparison-wrap">
              {buildRows(data, perspective).map((row) => (
                <div key={row.title} className="match-comparison" role="group" aria-label={row.title}>
                  <div className={`left-number-wrapper ${row.highlightLeft ? "stat-highlight" : ""}`}>
                    <p className="number">{row.leftValue}</p>
                  </div>
                  <p className="title">{row.title}</p>
                  <div className={`right-number-wrapper ${row.highlightRight ? "stat-highlight" : ""}`}>
                    <p className="number">{row.rightValue}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </section>
    </>
  );

  return (
    <section className="each-other-wrapper" aria-label="Head-to-head comparison">
      <div className="filter-wrapper" role="group" aria-label="Stats scope filter">
        <div className="filter-wrap" ref={filterRef}>
          <div className="chip-wrapper">
            <p className="chip-text">{chipText}</p>
          </div>
          <img
            src={Images.analysis_dropdown}
            alt=""
            aria-hidden="true"
            className="dropdown-icon"
            onClick={() => setIsFilterOpen((value) => !value)}
          />
          {isFilterOpen && (
            <section
              id={panelId}
              className="filter-compare"
              role="dialog"
              aria-label="Filter head-to-head scope"
              aria-modal="false"
            >
              <div className="main-filter-wrap">
                <div className="filter-compare-child">
                  <div className="left-selection">
                    <div className="select-type-parent">
                      <div className="select-type active">
                        <p className="format">Scope</p>
                      </div>
                    </div>
                  </div>
                  <div className="right-radio-parent">
                    {FILTER_OPTIONS.map((option) => (
                      <label key={option.value} className="radio-inputs">
                        <input
                          type="radio"
                          name="head-to-head-scope"
                          checked={pendingStatsType === option.value}
                          onChange={() => setPendingStatsType(option.value)}
                        />
                        <span className="filter-option-copy">
                          <p>{option.label}</p>
                          <small>{option.helper}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="filter-footer">
                  <button type="button" className="reset-button" onClick={handleReset}>
                    Reset
                  </button>
                  <button type="button" className="apply-button" onClick={handleApply}>
                    Apply
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {renderSection({
        sectionTitle: "Head-to-head stats with left player batting",
        leftLabel: "As Batter",
        rightLabel: "As Bowler",
        leftIcon: Images.asBatter_icon,
        rightIcon: Images.asBowl_icon,
        data: batterVsBowlerQuery.data,
        isLoading: batterVsBowlerQuery.isLoading,
        isError: batterVsBowlerQuery.isError,
        batsmanName: leftPlayer.name,
        bowlerName: rightPlayer.name,
        perspective: "left-batting",
      })}

      {renderSection({
        sectionTitle: "Head-to-head stats with right player batting",
        leftLabel: "As Bowler",
        rightLabel: "As Batter",
        leftIcon: Images.asBowl_icon,
        rightIcon: Images.asBatter_icon,
        data: bowlerVsBatterQuery.data,
        isLoading: bowlerVsBatterQuery.isLoading,
        isError: bowlerVsBatterQuery.isError,
        batsmanName: rightPlayer.name,
        bowlerName: leftPlayer.name,
        perspective: "right-batting",
      })}
    </section>
  );
};

export default EachOtherTab;
